import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Leistungen", id: "leistungen" },
  { label: "Vorher · Nachher", id: "vorher-nachher" },
  { label: "Ablauf", id: "ablauf" },
  { label: "Kontakt", id: "kontakt" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    // Delay so mobile menu close animation completes; use Lenis if available
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 70;
      if (window.__lenis?.scrollTo) {
        window.__lenis.scrollTo(top, { duration: 1.1 });
      } else {
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, 320);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-yx-bg/70 border-b border-yx-border/70"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between h-16 sm:h-20">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3"
        >
          <img
            src="/yardtf-logo-transparent.png"
            alt="YardTF Gartenservice"
            className="h-9 sm:h-11 w-auto object-contain"
          />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-sm font-semibold text-yx-primary hover:text-yx-secondary transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-yx-primary transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            data-testid="nav-cta"
            onClick={() => scrollTo("kontakt")}
            className="rounded-full bg-yx-primary px-6 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-yx-secondary hover:shadow-lg hover:shadow-yx-primary/20"
          >
            Anfrage senden
          </button>
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          <button
            data-testid="nav-mobile-cta-top"
            onClick={() => scrollTo("kontakt")}
            className="rounded-full bg-yx-primary px-4 py-2 text-xs font-semibold text-white transition-all duration-300 hover:bg-yx-secondary"
          >
            Anfrage
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-yx-primary p-1"
            aria-label="Menü"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-yx-bg/95 backdrop-blur-xl border-b border-yx-border"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => scrollTo(l.id)}
                  className="text-left text-lg font-medium text-yx-text"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta"
                onClick={() => scrollTo("kontakt")}
                className="rounded-full bg-yx-primary px-6 py-3 text-sm font-semibold text-white text-center"
              >
                Anfrage senden
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
